import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardMedia,
  Button,
  Chip,
  Divider,
  Box,
  Paper,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  Menu,
  MenuItem,
  TextField,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Schedule,
  Share,
  Room,
  Event as EventIcon,
  Payment,
  Group,
  Business as BusinessIcon,
  MoreVert,
  Flag,
  Email,
  Facebook,
  Twitter,
  LinkedIn,
  Link as LinkIcon,
  WhatsApp,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import EventRegistrationModal from './EventRegistrationModal';
import AddToCalendarModal from './AddToCalendar';
import EventPolicyComponent from './EventPolicyComponent';
import SuspiciousReportDialog from '../common/SuspiciousReportDialog';
import DisputesList from '../disputes/DisputesList';
import 'maplibre-gl/dist/maplibre-gl.css';
import MapWrapper from '../common/MapWrapper';
import parse from 'html-react-parser';
import { formatDate, formatTime, getTimezoneAbbreviation } from '../../utils/dateUtils';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { useAuth } from '../../contexts/AuthContext';
import { getDisputesByEvent } from '../../services/dispute';

const EventDetailsComponent = ({ event, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Disputes state
  const [disputes, setDisputes] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  
  const isOrganizer = event && user && event.organizerUuid === user.userUuid;
  
  // Fetch disputes for organizer
  React.useEffect(() => {
    if (event && isOrganizer) {
      fetchDisputes();
    }
  }, [event, isOrganizer]);

  const fetchDisputes = async () => {
    try {
      setDisputesLoading(true);
      const response = await getDisputesByEvent(event.id, 0, 100);
      setDisputes(response.content || []);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setDisputesLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{
        py: 4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" color="textPrimary">Event not found</Typography>
      </Container>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleSave = () => {
    setIsSaved(!isSaved);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleReportClick = () => {
    handleMenuClose();
    setReportDialogOpen(true);
  };

  const handleReportSuccess = (message) => {
    setReportDialogOpen(false);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleReportClose = () => {
    setReportDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Share functionality
  const eventUrl = `${window.location.origin}/events/${event.id}`;
  const shareText = `Check out this event: ${event.title}`;

  const handleShareClick = async () => {
    if (navigator.share) {
      // Use Web Share API for supported devices
      try {
        await navigator.share({
          title: event.title,
          text: shareText,
          url: eventUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to custom share dialog if Web Share API fails
          setShareDialogOpen(true);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      setShareDialogOpen(true);
    }
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setCopySuccess(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = eventUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  const shareViaEmail = () => {
    const subject = `Check out this event: ${event.title}`;
    const body = `${shareText}\n\n${eventUrl}\n\n${event.description?.substring(0, 200)}...`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(shareText);
    const url = encodeURIComponent(eventUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(eventUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(eventUrl);
    const title = encodeURIComponent(event.title);
    const summary = encodeURIComponent(event.description?.substring(0, 200) || '');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`${shareText} ${eventUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const DetailItem = ({ icon, title, value, iconBoxSize = 40  }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{
        backgroundColor: theme.palette.primary.light,
        borderRadius: '50%',
        width: iconBoxSize,
        height: iconBoxSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        mr: 2
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" color="textSecondary">
          {title}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  const LocationContent = () => {
    switch (event.locationType) {
      case 'physical':
        return (
          <>
            <DetailItem
              icon={<Room />}
              title="Location"
              value={
                <Box>
                  <Typography variant="body2">{event.fullLocationAddress.building}</Typography>
                  <Typography variant="body2">
                    {event.fullLocationAddress.street} 
                    {event.fullLocationAddress.suite && `, Suite ${event.fullLocationAddress.suite} `}
                  </Typography>
                  <Typography variant="body2">
                    {event.fullLocationAddress.city}, {event.fullLocationAddress.state} {event.fullLocationAddress.zipcode}
                  </Typography>
                  <Typography variant="body2">{event.fullLocationAddress.country}</Typography>
                </Box>
              }
            />
            <MapWrapper
              longitude={-75.5466}
              latitude={39.7456}
              height={300}
            />
          </>
        );
      case 'remote':
        return (
          <>
            <DetailItem
              icon={<Room />}
              title="Location"
              value='Event is virtual. Join link will be provided upon registration.'
            />
          </>
        );
      default:
        return <Typography>Unknown location type.</Typography>;
    }
  };

  return (
    <Box>

      {/* Event Image with Chips Overlay */}
      <Box sx={{ position: 'relative', mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardMedia
            component="img"
            height={isMobile ? 240 : 400}
            image={event.image}
            alt={event.title}
            sx={{
              objectFit: 'cover',
              width: '100%'
            }}
          />

          {/* Chips Overlay */}
          <Box sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'flex-start'
          }}>
            <Chip
              label={event.category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
              color="primary"
              sx={{
                fontWeight: 'bold',
                borderRadius: 2,
                px: 0.5,
                backgroundColor: 'rgba(25, 118, 210, 0.9)',
                color: 'white',
                backdropFilter: 'blur(4px)'
              }}
            />
            {event.isFeatured && (
              <Chip
                label="Featured"
                color="secondary"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: 2,
                  px: 1,
                  backgroundColor: 'rgba(156, 39, 176, 0.9)',
                  color: 'white',
                  backdropFilter: 'blur(4px)'
                }}
              />
            )}
          </Box>

          {/* Action Buttons Overlay */}
          <Box sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            gap: 1
          }}>
            <IconButton
              onClick={handleShareClick}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                width: 32, // Set width
                height: 32, // Set height
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)'
                }
              }}
            >
              <Share sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                width: 32, // Set width
                height: 32, // Set height
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)'
                }
              }}
            >
              <MoreVert sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Share Event
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Share this event with your friends and colleagues
          </Typography>

          {/* Share Options */}
          <List>
            <ListItem button onClick={copyToClipboard}>
              <ListItemIcon>
                {copySuccess ? <CheckCircle color="success" /> : <ContentCopy />}
              </ListItemIcon>
              <ListItemText
                primary={copySuccess ? "Copied!" : "Copy link"}
                secondary="Copy event URL to clipboard"
              />
            </ListItem>

            <ListItem button onClick={shareViaEmail}>
              <ListItemIcon>
                <Email />
              </ListItemIcon>
              <ListItemText primary="Share via Email" secondary="Send via email" />
            </ListItem>

            <ListItem button onClick={shareViaWhatsApp}>
              <ListItemIcon>
                <WhatsApp />
              </ListItemIcon>
              <ListItemText primary="Share on WhatsApp" secondary="Share with WhatsApp contacts" />
            </ListItem>

            <ListItem button onClick={shareViaFacebook}>
              <ListItemIcon>
                <Facebook />
              </ListItemIcon>
              <ListItemText primary="Share on Facebook" secondary="Post to Facebook" />
            </ListItem>

            <ListItem button onClick={shareViaTwitter}>
              <ListItemIcon>
                <Twitter />
              </ListItemIcon>
              <ListItemText primary="Share on Twitter" secondary="Tweet about this event" />
            </ListItem>

            <ListItem button onClick={shareViaLinkedIn}>
              <ListItemIcon>
                <LinkedIn />
              </ListItemIcon>
              <ListItemText primary="Share on LinkedIn" secondary="Share with professional network" />
            </ListItem>
          </List>

          {/* Direct Link Copy Field */}
          <TextField
            fullWidth
            variant="outlined"
            value={eventUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={copyToClipboard} edge="end">
                    {copySuccess ? <CheckCircle color="success" /> : <ContentCopy />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleShareDialogClose} sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <MenuItem onClick={handleReportClick} sx={{ color: theme.palette.error.main }}>
          <Flag sx={{ mr: 1, fontSize: 20 }} />
          Report Event
        </MenuItem>
      </Menu>

      {/* Rest of the component remains exactly the same */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          textAlign: 'left',
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 3
        }}
      >
        {event.title}
      </Typography>

      {/* Main Content Layout */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 4,
        alignItems: 'flex-start'
      }}>
        {/* Main Content */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          width: '100%'
        }}>
          {/* Event Tabs */}
          <Paper elevation={0} sx={{
            mb: 4,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48
                }
              }}
            >
              <Tab label="Event Details" />
              <Tab label="Venue" />
              {isOrganizer && <Tab label="Disputes" />}
            </Tabs>
          </Paper>

          {tabValue === 0 && (
            <Box>

              {event?.speakers?.length > 0 && (
                <Paper sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2,
                }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 550,
                    mb: 2,
                    mt: 2,
                    color: theme.palette.text.primary
                  }}>
                    Featured Speakers
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    mb: 1
                  }}>
                    {event?.speakers.map(speaker => (
                      <Box key={speaker.id} sx={{
                        p: 1,
                        // border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        textAlign: 'center',
                        width: isMobile ? '100%' : 200
                      }}>
                        <Avatar
                          src={speaker.image}
                          sx={{ width: 80, height: 80, mb: 1, mx: 'auto' }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {speaker.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {speaker.organizationRole}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {speaker.organization}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}

              {event.keyTakeaways?.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.text.primary
                  }}>
                    Key Takeaways:
                  </Typography>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: 2,
                    mb: 2
                  }}>
                    {event.keyTakeaways.map((item, index) => (
                      <Paper elevation={0} key={index} sx={{
                        p: 1,
                        height: '100%',
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        backgroundColor: theme.palette.background.paper
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              <Paper sx={{ p: 2, mb: 4 }}>
                {parse(event.description)}
              </Paper>

              {/* Event Tags Section */}
              {event.tags && event.tags.length > 0 && (
                <>
                  <Typography variant="body1" sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: theme.palette.text.primary
                  }}>
                    Tags
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2
                  }}>
                    {event.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          borderColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light + '33'
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
              {/* Event Policy Section */}
              <EventPolicyComponent eventPolicy={event.eventPolicy} />
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="body1" sx={{
                fontWeight: 700,
                mb: 3,
                color: theme.palette.text.primary
              }}>
                Venue Information
              </Typography>
              <LocationContent />
            </Box>
          )}
          
          {tabValue === 2 && isOrganizer && (
            <DisputesList
              disputes={disputes}
              loading={disputesLoading}
              title="Event Disputes"
              emptyMessage="No disputes filed against this event"
              showFiler={true}
              showRespondent={false}
            />
          )}
        </Box>

        {/* Sidebar Content */}
        {!isMobile && (
          <Box sx={{
            width: 300,
            position: 'sticky',
            top: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="body1" sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: 'center',
                color: theme.palette.text.primary
              }}>
                Event Registration
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <DetailItem
                icon={<EventIcon fontSize="small" />}
                iconBoxSize={35}
                title="Date"
                value={`${formatDate(event.date, { timeZone: event.timeZone })}`}
              />

              <DetailItem
                icon={<Schedule />}
                iconBoxSize={35}
                title="Time"
                value={`${formatTime(event.date, event.startTime, { timeZone: event.timeZone })} (${getTimezoneAbbreviation(event.timeZone)})`}
              />
              <DetailItem icon={<Room />} iconBoxSize={35} title="Venue" value={event.locationType === 'remote' ? 'Online' : event.location} />
              <DetailItem icon={<Payment />} iconBoxSize={35} title="Price" value={event.isFree ? 'Free' : `${getCurrencySymbol(event.currency)}${event.price}`} />


              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={() => setRegistrationOpen(true)}
                sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
              >
                Register Now
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => setCalendarModalOpen(true)}
                sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
              >
                Add to Calendar
              </Button>
            </Paper>

            {/* Organized By Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="body1" sx={{
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                color: theme.palette.text.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}>
                <BusinessIcon fontSize="small" />
                Organized by
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                textAlign: 'center'
              }}>
                <Avatar
                  src={event.organizer.image}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 2,
                    boxShadow: theme.shadows[2]
                  }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {event.organizer.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {event.organizer.bio}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Mobile Registration Section */}
      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
          <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              Event Registration
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <DetailItem icon={<EventIcon fontSize="small" />} title="Date" value={formatDate(event.date, { timeZone: event.timeZone })} />
            <DetailItem icon={<Schedule />} title="Time" value={`${formatTime(event.date, event.startTime, { timeZone: event.timeZone })} - ${formatTime(event.date, event.endTime, { timeZone: event.timeZone })}`} />
            <DetailItem icon={<Room />} title="Venue" value={event.location} />
            <DetailItem icon={<Payment />} title="Price" value={event.isFree ? 'Free' : `${getCurrencySymbol(event.currency)}${event.price}`} />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => setRegistrationOpen(true)}
              sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
            >
              Register Now
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => setCalendarModalOpen(true)}
              sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
            >
              Add to Calendar
            </Button>
          </Paper>

          {/* Organized By Section - Mobile */}
          <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body1" sx={{
              fontWeight: 600,
              mb: 2,
              textAlign: 'center',
              color: theme.palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <BusinessIcon fontSize="small" />
              Organized by
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              textAlign: 'center'
            }}>
              <Avatar
                src={event.organizer.image}
                sx={{
                  width: 80,
                  height: 80,
                  mb: 2,
                  boxShadow: theme.shadows[2]
                }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {event.organizer.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {event.organizer.bio}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      <EventRegistrationModal
        event={event}
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
      />

      <AddToCalendarModal
        event={event}
        open={calendarModalOpen}
        onClose={() => setCalendarModalOpen(false)}
      />

       {/* Suspicious Report Dialog */}
       <SuspiciousReportDialog
        open={reportDialogOpen}
        onClose={handleReportClose}
        onReportSuccess={handleReportSuccess}
        entityType="event"
        entityId={event.id}
        entityTitle={event.title}
      />
    </Box>
  );
};

export default EventDetailsComponent;